import React, { FC, memo, useEffect, useRef, useState } from 'react';
import '../style.scss';
import TableSkeleton from '@uikit/Loader/TableSkeleton/TableSkeleton';
import { ColumnsTableSkeleton } from '@uikit/Loader/ColumnsTableSkeleton/ColumnsTableSkeleton';
import { useAppDispatch } from '@store/hooks';
import Pagination from '@uikit/Pagination/Pagination';
import { Column } from '@uikit/Tables/types';
import { useColumnOrder } from './hooks/useColumnOrder';
import { useTablePagination } from './hooks/useTablePagination';
import TableHeader from './components/TableHeader';
import TableBody from './components/TableBody';
import { CustomTableProps } from '@uikit/Tables/TableDynamic/constants';

const TableDynamic: FC<CustomTableProps> = ({
  isLoading = false,
  modalName,
  path,
  totalPages,
  page = 0,
  totalElements,
  size,
  columns,
  onEditClick,
  onAuditClick,
  onImageClick,
  onPageChange,
  className,
  abilities,
  activeRowId,
  handleSelectedRow,
  handleDeleteRow,
  rows,
  onFieldClick,
  tableName,
  requestTablePageChange,
  openModal,
  tableCustomClassName,
  columnsSorting,
  sortingTable,
  setSortingTable,
  hasPagination = true,
  method,
  argument,
  params,
  skeletonProps,
  columnsSkeleton = false,
  tableIsBig,
  statusSetter,
  onUpdate,
  dataTestId,
  scrollToTopOnDataChange = true,
  emptyText = 'No Data Found',
}) => {
  const dispatch = useAppDispatch();

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const [selectedItem, setSelectedItem] = useState(activeRowId ?? null);
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);

  const { columnOrder, setColumnOrder, control } = useColumnOrder(columns);

  const {
    updatedPage,
    setPage,
    isUpdating,
    isRefreshing,
    handlePageChange,
    handleSizeChange,
    handleRefresh,
  } = useTablePagination({
    page,
    size,
    path,
    params,
    sortingTable,
    tableName,
    columnsSorting,
    argument,
    requestTablePageChange,
    onPageChange,
  });

  useEffect(() => {
    setSelectedItem(activeRowId ?? null);
  }, [activeRowId]);
  useEffect(() => {
    setPage(page);
  }, [page]);

  useEffect(() => {
    if (scrollToTopOnDataChange && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = 0;
    }
  }, [rows, scrollToTopOnDataChange]);

  const handleSorting = (col: Column) => {
    if (method === 'GET' || !col.isSortable) return;

    setSortingTable((prevSorting) => {
      const newSortDir =
        prevSorting.sortBy === col.sortingField &&
        prevSorting.sortDir === 'desc'
          ? 'asc'
          : 'desc';

      const updatedSorting = { sortBy: col.sortingField, sortDir: newSortDir };

      requestTablePageChange &&
        dispatch(
          requestTablePageChange.initiate(
            {
              requestId: Date.now(),
              id: 2,
              pathName: path,
              type: path,
              argument: argument ?? {},
              ...(argument ?? {}),
              params: { page, size, ...params, ...updatedSorting },
              body: tableName ? columnsSorting[tableName] : {},
            },
            { forceRefetch: true }
          )
        );

      return updatedSorting;
    });
  };

  const showPagination =
    hasPagination &&
    !isNaN(page ?? 0) &&
    !isNaN(totalElements ?? 0) &&
    !isNaN(size ?? 0) &&
    !isNaN(totalPages ?? 0);

  const hasTableData = rows?.length > 0;
  return (
    <div
      ref={tableContainerRef}
      className={`table-container ${tableIsBig ? 'big_table' : ''} ${tableCustomClassName ?? ''}`}
      data-test-id={dataTestId}
    >
      {isLoading && columnsSkeleton ? (
        <ColumnsTableSkeleton
          columns={columnOrder}
          rows={size || undefined}
          options={typeof columnsSkeleton === 'object' ? columnsSkeleton : {}}
        />
      ) : isLoading ? (
        <TableSkeleton
          columns={columnOrder?.length || undefined}
          rows={size || undefined}
          showPagination={showPagination}
          {...skeletonProps}
        />
      ) : (
        <div
          className={`custom-table-wrapper ${isRefreshing || isUpdating ? 'is-refreshing' : ''}`}
        >
          <div ref={tableScrollRef} className="t-scroll">
            {hasTableData ? (
              <table
                ref={tableRef}
                className={`custom-table ${isUpdating || isRefreshing ? 'blured' : ''} ${className}`}
              >
                <TableHeader
                  columnOrder={columnOrder}
                  setColumnOrder={setColumnOrder}
                  columns={columns}
                  control={control}
                  tableRef={tableRef}
                  hoveredCol={hoveredCol}
                  sortingTable={sortingTable}
                  method={method}
                  setHoveredCol={setHoveredCol}
                  onSorting={handleSorting}
                />

                <TableBody
                  rows={rows}
                  columnOrder={columnOrder}
                  selectedItem={selectedItem}
                  abilities={abilities}
                  modalName={modalName}
                  tableName={tableName}
                  openModal={openModal}
                  onEditClick={onEditClick}
                  onAuditClick={onAuditClick}
                  onImageClick={onImageClick}
                  onFieldClick={onFieldClick}
                  handleSelectedRow={handleSelectedRow}
                  handleDeleteRow={handleDeleteRow}
                  statusSetter={statusSetter}
                  onUpdate={onUpdate}
                  setSelectedItem={setSelectedItem}
                />
              </table>
            ) : (
              <div className="table-no-rows no-data-found">{emptyText}</div>
            )}
          </div>

          {showPagination && (
            <Pagination
              size={size}
              totalElements={totalElements}
              itemsCount={rows.length}
              currentPage={updatedPage + 1}
              handlePageChange={handlePageChange}
              handleSizeChange={handleSizeChange}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              dataTestId={dataTestId}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(TableDynamic);
